import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import App from './App';
import AppAdvanced from './App_advanced_min';
import AccumulationWrapper from './AccumulationWrapper';
import WorkingAccumulation from './working-accumulation';
import SMSFComprehensiveUI from './SMSFUIIntegration';
import { AdvancedStateProvider } from './AdvancedState';
import DisclaimerModal from './components/DisclaimerModal.js';
import { PrivacyPolicy, TermsOfService, DisclaimerPage, CompliancePage } from './components/LegalPages.js';
import { CalculatorSelectionGuide } from './components/CalculatorSelectionGuide.js';
import CalculatorTransparencyPage from './components/CalculatorTransparencyPage.js';
import HelpCenter from './components/HelpCenter.js';
import PaymentFlowTester from './components/PaymentFlowTester.js';
import BrowserCompatibilityTester from './components/BrowserCompatibilityTester.js';
import AccessibilityTester from './components/AccessibilityTester.js';
import { SubscriptionManager } from './SubscriptionManager.js';
import { shouldShowLocalDevChrome, isOfflineLocalServer } from './utils/presentationMode.js';
import { SubscriptionBadge, UpgradeButton } from './components/PremiumFeatureGate.js';
import UserTestimonialsSection from './components/UserTestimonialsSection.js';
import { SubscriptionManagement } from './components/SubscriptionManagement.js';
import MobileNavigation from './components/MobileNavigation.js';
import PWAInstallPrompt from './components/PWAInstallPrompt.js';
import registerServiceWorker from './utils/registerServiceWorker.js';
import MobileCalculatorLanding from './components/MobileCalculatorLanding.js';
import BasicCalculatorMobile from './components/BasicCalculatorMobile.js';
import AccumulationCalculatorMobile from './components/AccumulationCalculatorMobile.js';
import AdvancedCalculatorMobile from './components/AdvancedCalculatorMobile.js';
import SMSFCalculatorMobile from './components/SMSFCalculatorMobile.js';
import EmailSignup from './components/EmailSignup.js';
import UpdateMarker from './components/UpdateMarker.js';
import { ExitIntentApp } from './components/ExitIntentApp.js';
import { openStripeCustomerPortal } from './openStripeCustomerPortal.js';

const MainNavigation = () => {
  // Initialize currentView from hash if present
  const getInitialView = () => {
    const hash = window.location.hash.substring(1); // e.g., "advanced?scenario=xyz"
    const hashBase = hash.split('?')[0]; // Strip parameters, get just "advanced"
    const validCalculators = ['basic', 'advanced', 'working-accumulation', 'smsf'];
    const validPages = ['transparency'];
    return (hashBase && (validCalculators.includes(hashBase) || validPages.includes(hashBase))) ? hashBase : 'home';
  };
  
  const [currentView, setCurrentView] = useState(getInitialView());
  const [showDisclaimer, setShowDisclaimer] = useState(false);
  const [showBillingModal, setShowBillingModal] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  // Silently verify subscription with Stripe on app load (once per 23h).
  // Skip on local offline demo — mock API returns null and real sub IDs are irrelevant.
  useEffect(() => {
    if (isOfflineLocalServer()) return;

    const subId    = localStorage.getItem('subscription_id') || '';
    const subTier  = localStorage.getItem('subscription_tier') || '';
    const paidTiers = ['pro', 'essential', 'premium'];

    // Only run for real Stripe subscription IDs on paid tiers
    if (!subId.startsWith('sub_') || !paidTiers.includes(subTier)) return;

    // Cooldown: check at most once every 23 hours
    const lastCheck = parseInt(localStorage.getItem('subscription_last_verified') || '0', 10);
    if (Date.now() - lastCheck < 23 * 60 * 60 * 1000) return;

    fetch('/.netlify/functions/verify-subscription', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ subscriptionId: subId })
    })
      .then(r => r.json())
      .then(data => {
        // Server error — do nothing, don't penalise the user
        if (data.active === null) return;

        if (data.active) {
          // Refresh expiry to Stripe's real billing period end
          if (data.currentPeriodEnd) {
            const expiry = new Date(data.currentPeriodEnd * 1000);
            localStorage.setItem('subscription_expiry', expiry.toISOString());
          }
          localStorage.setItem('subscription_last_verified', String(Date.now()));
        } else {
          // Subscription cancelled or payment failed in Stripe — remove access
          SubscriptionManager.downgradeToFree();
          localStorage.removeItem('subscription_last_verified');
          window.location.reload();
        }
      })
      .catch(() => {
        // Network error — do nothing, keep existing access
      });
  }, []);

  // If user landed with ?session_id=... (e.g. support sent them to fix "paid but locked" bug), verify and unlock
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get('session_id');
    if (!sessionId) return;
    fetch(`/.netlify/functions/get-checkout-session?session_id=${encodeURIComponent(sessionId)}`)
      .then(r => r.json())
      .then(data => {
        if (data.session && data.session.status === 'paid') {
          const session = data.session;
          const sub = data.subscription || {};
          const cycle = (session.cycle || 'monthly').toLowerCase();
          const isAnnual = cycle === 'annual' || cycle === 'year';
          SubscriptionManager.upgradeToProTier({
            subscriptionId: sub.id || session.id || sessionId,
            interval: isAnnual ? 'year' : 'month',
            amount: session.amount
          });
          SubscriptionManager.initialize();
          const cleanUrl = window.location.pathname + (window.location.hash || '');
          window.history.replaceState({}, '', cleanUrl);
          window.location.reload();
        }
      })
      .catch(() => {});
  }, []);

  // Check if user has accepted disclaimer on mount
  // Using sessionStorage so disclaimer shows every new session (tab/window)
  useEffect(() => {
    const disclaimerAccepted = sessionStorage.getItem('calculator_disclaimer_accepted');
    if (!disclaimerAccepted) {
      setShowDisclaimer(true);
    }
  
    // Register service worker for PWA
    // TEMPORARILY DISABLED - Service Worker was caching old build and preventing bug fixes from deploying
    // registerServiceWorker();
  }, []);

  // Track screen size for mobile optimization
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Listen for hash changes to support navigation from SMSF and other calculators
  useEffect(() => {
    const handleHashChange = () => {
      const hash = window.location.hash.substring(1); // Remove the '#', e.g., "advanced?scenario=xyz"
      const hashBase = hash.split('?')[0]; // Strip parameters, get just "advanced"
      if (hashBase && (calculators.some(calc => calc.id === hashBase) || ['transparency'].includes(hashBase))) {
        console.log(`🔀 Hash navigation detected: ${hashBase}`);
        setCurrentView(hashBase);
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    };
    
    // Check initial hash on mount
    handleHashChange();
    
    // Listen for hash changes
    window.addEventListener('hashchange', handleHashChange);
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, []);

  // Track user subscription type on mount
  useEffect(() => {
    if (typeof gtag !== 'undefined' && typeof window.SubscriptionManager !== 'undefined') {
      const tier = window.SubscriptionManager.getCurrentTier();
      const pricing = window.SubscriptionManager.PRICING;
      const isTrialActive = window.SubscriptionManager.hasActiveTrial();
      const trialInfo = isTrialActive ? window.SubscriptionManager.getTrialInfo() : null;
      
      // Map tier to user_type
      let userType = 'free_user';
      let monthlyValue = 0;
      
      if (isTrialActive) {
        // User is on trial
        userType = tier === 'premium' ? 'trial_premium' : 'trial_essential';
        monthlyValue = tier === 'premium' ? pricing.PREMIUM_MONTHLY : pricing.ESSENTIAL_MONTHLY;
        
        // Track trial status
        gtag('event', 'trial_active', {
          'trial_tier': tier,
          'days_remaining': trialInfo.daysRemaining,
          'trial_code': trialInfo.code
        });
      } else if (tier === 'essential' || tier === 'pro') {
        userType = 'subscriber_essential';
        monthlyValue = pricing.ESSENTIAL_MONTHLY;
      } else if (tier === 'premium') {
        userType = 'subscriber_premium';
        monthlyValue = pricing.PREMIUM_MONTHLY;
      }
      
      // Set as user property (persists across all events)
      gtag('set', 'user_properties', {
        'user_type': userType,
        'subscription_tier': tier,
        'monthly_value': monthlyValue,
        'is_trial': isTrialActive
      });
      
      console.log('📊 GA4: User type set -', userType, tier, '$' + monthlyValue, isTrialActive ? '(TRIAL)' : '');
    }
  }, []);

  // Track calculator tier usage
  useEffect(() => {
    const calculatorTiers = {
      'working-accumulation': 'accumulation',
      'basic': 'basic',
      'advanced': 'advanced',
      'smsf': 'smsf'
    };
    
    const tier = calculatorTiers[currentView];
    if (tier && typeof gtag !== 'undefined') {
      // Standard funnel event — Step 1: user sees the calculator
      gtag('event', 'calculator_view', {
        'event_category': 'funnel',
        'calculator_tier': tier,
        'calculator_name': currentView,
        'ab_variant': window._abVariant || 'control'
      });

      // Also keep calculator-specific event names for GA4 exploration
      gtag('event', `calculator_view_${tier}`, {
        'event_category': 'Calculator Usage',
        'event_label': currentView
      });
      
      // Also track generic event with parameters for custom dimension setup
      gtag('event', 'calculator_tier_view', {
        'calculator_tier': tier,
        'calculator_name': currentView,
        'event_category': 'Calculator Tier Usage',
        'timestamp': new Date().toISOString()
      });
      
      // Track calculator load separately for better segmentation
      gtag('event', 'calculator_loaded', {
        'calculator_name': currentView,
        'calculator_tier': tier,
        'is_premium': tier === 'advanced' || tier === 'smsf',
        'user_type': window.SubscriptionManager ? window.SubscriptionManager.getCurrentTier() : 'free'
      });
      
      console.log('📊 GA4: Calculator loaded -', currentView, '(tier:', tier + ')');
    }
  }, [currentView]);

  const handleAcceptDisclaimer = () => {
    sessionStorage.setItem('calculator_disclaimer_accepted', 'true');
    sessionStorage.setItem('calculator_disclaimer_accepted_date', new Date().toISOString());
    setShowDisclaimer(false);
  };

  const handleDeclineDisclaimer = () => {
    alert('You must accept the disclaimer to use the calculator. If you decline, you will not be able to access the calculators.');
    // Could redirect to an external site or show explanation
  };

  const calculators = [
    {
      id: 'working-accumulation',
      title: '1. Accumulation Calculator',
      description: 'Track superannuation growth during working years with salary sacrifice, contributions, and compound interest projections',
      component: WorkingAccumulation,
      color: 'purple',
      tier: 'free',
      badge: 'FREE'
    },
    {
      id: 'basic',
      title: '2. Basic Retirement Calculator',
      description: 'Simple retirement snapshot with Age Pension estimates, super balance projections, and retirement income planning',
      component: App,
      color: 'blue',
      tier: 'free',
      badge: 'FREE'
    },
    {
      id: 'advanced',
      title: '3. Advanced Retirement Calculator',
      description: 'Stress-test your retirement plan against market crashes, inflation spikes, bad timing, Age Pension rules, lump sums, and phased retirement',
      component: AppAdvanced,
      color: 'green',
      tier: 'pro',
      badge: 'PRO',
      feature: SubscriptionManager.FEATURES.ADVANCED_CALCULATOR
    },
    {
      id: 'smsf',
      title: '4. SMSF Calculator Suite',
      description: 'SMSF calculator suite with 36 specialized tools including LRBA analysis, Division 293 tax, and planning features',
      component: SMSFComprehensiveUI,
      color: 'sky',
      tier: 'pro',
      badge: 'PRO',
      feature: SubscriptionManager.FEATURES.SMSF_SUITE
    }
  ];

  const handleBackToHome = () => {
    setCurrentView('home');
    // Scroll to top when navigating
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Navigate to legal/info pages with scroll to top
  const navigateToPage = (page) => {
    setCurrentView(page);
    if (typeof window !== 'undefined') {
      window.location.hash = page;
    }
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Render legal pages and guide
  const renderLegalPage = () => {
    switch (currentView) {
      case 'privacy':
        return <PrivacyPolicy />;
      case 'terms':
        return <TermsOfService />;
      case 'disclaimer':
        return <DisclaimerPage />;
      case 'compliance':
        return <CompliancePage />;
      case 'selection-guide':
        return <CalculatorSelectionGuide />;
      case 'transparency':
        return <CalculatorTransparencyPage />;
      case 'help':
        return <HelpCenter />;
      case 'payment-test':
        return <PaymentFlowTester />;
      case 'browser-test':
        return <BrowserCompatibilityTester />;
      case 'accessibility-test':
        return <AccessibilityTester />;
      default:
        return null;
    }
  };

  const renderCalculator = () => {
    const calculator = calculators.find(calc => calc.id === currentView);
    if (!calculator) return null;

    // MOBILE: Show mobile wizards for all calculators (gate Advanced and SMSF for non-subscribers)
    if (isMobile && currentView === 'basic') {
      return <BasicCalculatorMobile onBack={handleBackToHome} onGoToAdvanced={() => setCurrentView('advanced')} />;
    }
    if (isMobile && currentView === 'working-accumulation') {
      return <AccumulationCalculatorMobile onBack={handleBackToHome} />;
    }
    if (isMobile && currentView === 'advanced') {
      // Keep the calculator + last result visible when free runs are exhausted
      // (paywall overlays inside AdvancedCalculatorMobile — no wipe-to-lock-screen).
      return <AdvancedCalculatorMobile onBack={handleBackToHome} />;
    }
    if (isMobile && currentView === 'smsf') {
      const hasSmsf = SubscriptionManager.hasAccess(SubscriptionManager.FEATURES.SMSF_SUITE);
      if (!hasSmsf) {
        return (
          <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 p-6 flex flex-col">
            <button onClick={handleBackToHome} className="flex items-center text-gray-600 hover:text-gray-900 mb-6">
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
              Back to Calculators
            </button>
            <div className="flex-1 flex flex-col items-center justify-center text-center max-w-md mx-auto">
              <div className="text-5xl mb-4">🔒</div>
              <h2 className="text-xl font-bold text-gray-900 mb-2">SMSF Suite</h2>
              <p className="text-gray-700 mb-4">36 specialized SMSF tools, compliance, tax optimization and more. Unlock with All Access from $39.99/mo.</p>
              <p className="text-sm text-gray-600 mb-6">You can try example scenarios on a desktop computer without subscribing.</p>
              <UpgradeButton size="large" />
              <p className="text-xs text-gray-500 mt-4">
                Already subscribed?{' '}
                <a href="/restore-access.html" className="underline font-medium text-blue-600">
                  Restore access
                </a>
              </p>
            </div>
          </div>
        );
      }
      return <SMSFCalculatorMobile onBack={handleBackToHome} />;
    }

    const CalculatorComponent = calculator.component;
    
    // Wrap advanced calculator with AdvancedStateProvider
    const CalculatorWrapper = calculator.id === 'advanced' 
      ? () => (
          <AdvancedStateProvider>
            <CalculatorComponent navigateToCalculator={setCurrentView} />
          </AdvancedStateProvider>
        )
      : () => <CalculatorComponent navigateToCalculator={setCurrentView} />;

    return (
      <div>
        <div className="bg-white shadow-sm border-b mb-6">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between h-16">
              <div className="flex items-center">
                <img src="/logo.png" alt="SuperCalc Pro Logo" className="w-8 h-8 rounded mr-3" />
                <button
                  onClick={handleBackToHome}
                  className="flex items-center text-gray-600 hover:text-gray-900 transition-colors"
                >
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                  Back to Calculators
                </button>
              </div>
              <h1 className="text-lg font-semibold text-gray-900">{calculator.title}</h1>
              <div className="flex items-center gap-3">
                <button
                  onClick={() => navigateToPage('transparency')}
                  className="hidden sm:inline-flex items-center gap-2 text-xs px-3 py-1.5 rounded-full bg-blue-50 text-blue-700 border border-blue-200 hover:bg-blue-100 font-semibold"
                >
                  What this calculator considers
                </button>
                {/* Localhost-only: toggle free user so run limit and email gate can be tested */}
                {shouldShowLocalDevChrome() && (
                  <button
                    type="button"
                    onClick={() => {
                      const isFree = localStorage.getItem('simulate_free_user') === '1';
                      if (isFree) {
                        localStorage.removeItem('simulate_free_user');
                      } else {
                        localStorage.setItem('simulate_free_user', '1');
                        localStorage.removeItem('subscription_tier');
                        localStorage.removeItem('subscription_expiry');
                        localStorage.removeItem('subscription_id');
                        localStorage.removeItem('subscription_start');
                        localStorage.removeItem('adv_anon_runs');
                        localStorage.removeItem('adv_acct_runs');
                      }
                      window.location.reload();
                    }}
                    className="text-xs px-2 py-1 rounded bg-amber-100 text-amber-800 border border-amber-300 hover:bg-amber-200 font-medium"
                    title={localStorage.getItem('simulate_free_user') === '1'
                      ? 'Simulating free user (gates active). Click to restore localhost All Access.'
                      : 'Localhost All Access (gates hidden). Click to simulate free user.'}
                  >
                    {localStorage.getItem('simulate_free_user') === '1' ? 'Dev mode: Free user' : 'Dev mode: All Access'}
                  </button>
                )}
                <UpdateMarker variant="compact" />
              </div>
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-3">
          <div className="bg-blue-50 border border-blue-200 rounded-lg px-4 py-2 text-xs sm:text-sm text-blue-900">
            All dollar figures shown in calculator results are inflation-adjusted and expressed in today's dollars, unless explicitly labelled otherwise.
          </div>
        </div>

        {/* Stress-test positioning for Advanced calculator */}
        {calculator.id === 'advanced' && (
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-6 mb-2">
            <div className="bg-gradient-to-r from-slate-50 to-blue-50 border border-slate-200 rounded-xl px-6 py-4 shadow-sm">
              <p className="text-lg font-bold text-slate-900">
                Don't just project retirement. Stress-test it.
              </p>
              <p className="text-sm text-slate-600 mt-1">
                Most retirement calculators assume average returns and smooth markets. Real retirement doesn't work that way.
              </p>
              <p className="text-sm text-slate-600 mt-1">
                See how your retirement holds up through market crashes, inflation spikes, bad timing, Age Pension changes, and major spending events.
              </p>
              <p className="text-sm font-semibold text-slate-700 mt-2">
                Because the question isn't whether your plan works in a good decade. It's whether it survives a bad one.
              </p>
              <p className="text-xs text-slate-500 mt-2">
                Built using 100 years of historical data and advanced Australian retirement modelling.
              </p>
            </div>
          </div>
        )}

        <CalculatorWrapper />
      </div>
    );
  };

  // Render legal pages and guide
  if (['privacy', 'terms', 'disclaimer', 'compliance', 'selection-guide', 'transparency', 'help', 'payment-test', 'browser-test', 'accessibility-test'].includes(currentView)) {
    return (
      <div>
        {/* Mobile Navigation */}
        {isMobile && <MobileNavigation currentView={currentView} setCurrentView={setCurrentView} calculators={calculators} />}
        
        <div className={`bg-white shadow-sm border-b mb-6 ${isMobile ? 'hidden' : ''}`}>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between h-16">
              <button
                onClick={handleBackToHome}
                className="flex items-center text-gray-600 hover:text-gray-900 transition-colors"
              >
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
                Back to Home
              </button>
            </div>
          </div>
        </div>
        {renderLegalPage()}
        
        {/* Footer for legal pages with navigation */}
        <footer className="bg-gray-100 mt-12 py-8 border-t">
          <div className="max-w-4xl mx-auto px-4">
            <div className="flex flex-wrap justify-center gap-4 mb-4">
              <button onClick={() => navigateToPage('privacy')} className="text-blue-600 hover:text-blue-800 text-sm font-medium">Privacy Policy</button>
              <span className="text-gray-400">•</span>
              <button onClick={() => navigateToPage('transparency')} className="text-blue-600 hover:text-blue-800 text-sm font-medium">Calculation Transparency</button>
              <span className="text-gray-400">•</span>
              <button onClick={() => navigateToPage('terms')} className="text-blue-600 hover:text-blue-800 text-sm font-medium">Terms of Service</button>
              <span className="text-gray-400">•</span>
              <a href="/refund-policy.html" className="text-blue-600 hover:text-blue-800 text-sm font-medium">Refund Policy</a>
              <span className="text-gray-400">•</span>
              <a href="/guides/" className="text-green-600 hover:text-green-800 text-sm font-medium">📚 Planning Guides</a>
            </div>
            <div className="text-center text-sm text-gray-600">
              © 2026 Superannuation Calculator Suite. All rights reserved.
            </div>
          </div>
        </footer>
      </div>
    );
  }

  if (currentView !== 'home') {
    return renderCalculator();
  }

  // Mobile: Show mobile calculator selection interface WITH bottom nav (Blog, More, etc.)
  if (isMobile && currentView === 'home') {
    return (
      <>
        <MobileCalculatorLanding 
          onSelectCalculator={setCurrentView} 
          currentCalculator={currentView}
          onShowGuide={() => navigateToPage('selection-guide')}
        />
        <MobileNavigation currentView={currentView} setCurrentView={setCurrentView} calculators={calculators} />
        <PWAInstallPrompt />
      </>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Exit-intent: "Before you go" on desktop home – once per session */}
      {currentView === 'home' && !isMobile && (
        <ExitIntentApp
          onTryAdvanced={() => {
            setCurrentView('advanced');
            window.scrollTo({ top: 0, behavior: 'smooth' });
          }}
        />
      )}
      {/* Mobile Navigation (shown on small screens) */}
      {isMobile && (
        <>
          <MobileNavigation currentView={currentView} setCurrentView={setCurrentView} calculators={calculators} />
          <PWAInstallPrompt />
        </>
      )}

      {/* Header (hidden on mobile) */}
      <div className={`bg-gradient-to-r from-blue-600 to-purple-600 shadow-lg ${isMobile ? 'hidden' : ''}`}>
        <div className="max-w-7xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-center mb-4">
            <img src="/logo.png" alt="SuperCalc Pro - Australia's Leading Retirement Planning Calculator" className="w-20 h-20 rounded-lg mr-4" />
            <h1 className="text-4xl font-bold text-white">
              SuperCalc Pro
            </h1>
          </div>
          <p className="text-2xl text-center text-blue-50 font-bold">
            Don't just project retirement. Stress-test it.
          </p>
          <p className="mt-3 text-center text-blue-100 text-base max-w-3xl mx-auto">
            An Australian retirement calculator suite that helps you see whether your plan survives market crashes, inflation spikes, bad timing, Age Pension changes, and real-life spending events.
          </p>
          <p className="mt-3 text-center text-blue-200 text-sm">
            Start with free calculators • One free Advanced run, then subscribe for PDF, saved scenarios, and unlimited historical stress-tests
          </p>
        </div>
      </div>

      {/* Update marker: weekly rates + recent product updates */}
      <UpdateMarker variant="banner" />

      {/* Calculator Grid */}
      <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8"
           style={{ paddingBottom: isMobile ? 'calc(80px + max(env(safe-area-inset-bottom), 0px))' : '' }}>
        {/* Choose Your Calculator - compact; calculators first so they're obvious */}
        <div className="flex justify-between items-center mb-4 flex-wrap gap-4">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Choose Your Calculator</h2>
            <p className="text-gray-600 mt-1 text-sm">Start simple, then stress-test the plan when the stakes are higher.</p>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            <a href="/guides/" className="px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition text-sm font-medium">📚 Planning Guides</a>
            <SubscriptionBadge />
          </div>
        </div>

        <div className="bg-gradient-to-r from-slate-900 to-blue-900 text-white rounded-2xl p-5 mb-6 shadow-lg flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h2 className="text-xl font-black">Calculation Transparency</h2>
            <p className="text-sm text-blue-100 mt-1">
              See the full list of inputs, rules, thresholds, and modelling assumptions each calculator can consider.
            </p>
          </div>
          <button
            type="button"
            onClick={() => navigateToPage('transparency')}
            className="px-5 py-3 rounded-xl bg-white text-blue-900 hover:bg-blue-50 font-bold text-sm shadow-sm"
          >
            View Transparency List
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-10"
             id="calculator-cards">
          {calculators.map((calculator) => {
            // Check if user has paid access
            const hasProAccess = SubscriptionManager.hasAccess(calculator.feature);
            const isProFeature = calculator.tier === 'pro';

            // All calculators are clickable - paid features open in demo mode for free users
            return (
              <div
                key={calculator.id}
                className="bg-white rounded-lg shadow-md hover:shadow-lg transition-all transform hover:-translate-y-1 cursor-pointer border border-gray-200 min-h-[230px] flex flex-col"
                onClick={() => {
                  setCurrentView(calculator.id);
                  // Scroll to top when opening calculator
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }}
              >
                <div className="p-6 flex-1 flex flex-col">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center">
                      <div className={`w-3 h-3 rounded-full bg-${calculator.color}-500 mr-3`}></div>
                      <h3 className="text-xl font-semibold text-gray-900">
                        {calculator.title}
                      </h3>
                    </div>
                    {calculator.tier === 'free' ? (
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-bold bg-green-100 text-green-800 border border-green-200">
                        FREE
                      </span>
                    ) : (
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-bold bg-gradient-to-r from-blue-600 to-purple-600 text-white shadow-lg">
                        <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                        </svg>
                        PAID
                      </span>
                    )}
                  </div>
                  <p className="text-gray-600 mb-4 flex-1">
                    {calculator.description}
                  </p>
                  
                  {/* Show what's included for paid features + pricing */}
                  {isProFeature && (
                    <div className="space-y-2 text-sm text-gray-600 mb-4">
                      <div className="flex items-center gap-2">
                        <svg className="w-4 h-4 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                        <span>{calculator.id === 'advanced' ? 'Phased retirement modelling' : '36 Specialized Tools'}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <svg className="w-4 h-4 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                        <span>{calculator.id === 'advanced' ? 'Historical stress-testing + Monte Carlo' : 'Compliance Monitoring'}</span>
                      </div>
                      <p className="text-xs font-semibold text-blue-700 mt-1">
                        {calculator.id === 'advanced'
                          ? '1 free run · then from $14.99/mo'
                          : 'Unlock from $39.99/mo'}
                      </p>
                    </div>
                  )}

                  <button
                    className={`w-full ${
                      isProFeature && !hasProAccess
                        ? 'bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700'
                        : `bg-${calculator.color}-600 hover:bg-${calculator.color}-700`
                    } text-white font-medium py-3 px-4 rounded-lg transition-colors shadow`}
                  >
                    Launch Calculator →
                  </button>
                </div>
              </div>
            );
          })}
        </div>

        <UserTestimonialsSection className="mb-10" />

        {/* Supporting content below calculators */}
        <div className="space-y-6 mb-10">
          <div className="bg-gradient-to-r from-emerald-50 to-teal-50 border-2 border-emerald-200 rounded-lg p-5 shadow-sm">
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-emerald-600 text-white flex items-center justify-center">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" /></svg>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Not sure how much income you need?</h3>
                  <p className="text-gray-700 text-sm mt-1">Build a detailed retirement budget (housing, utilities, food, transport, health, lifestyle) and get an indicative annual and monthly target. Then use the calculators above to see if you can fund it.</p>
                </div>
              </div>
              <a href="/calculators/retirement-income-needs-calculator.html" className="flex-shrink-0 inline-flex items-center px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition font-medium">Retirement income needs planner →</a>
            </div>
          </div>

          <div className="bg-gradient-to-r from-purple-50 to-indigo-50 border-2 border-purple-200 rounded-lg p-6 shadow-sm">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <svg className="h-8 w-8 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                </svg>
              </div>
              <div className="ml-4 flex-1">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">📖 Read Our Retirement Guides</h3>
                <p className="text-gray-700 mb-3 text-sm">Discover expert insights on Age Pension rules, superannuation strategies, and retirement planning. Learn about the 7.8% taper trap, Transfer Balance Cap limits, and how to maximize your retirement income.</p>
                <a href="/blog/" className="inline-flex items-center px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 transition-colors shadow-sm font-medium text-sm">View All Articles</a>
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-r from-blue-50 to-purple-50 border-2 border-blue-200 rounded-lg p-6 shadow-sm">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <svg className="h-8 w-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div className="ml-4 flex-1">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">🤔 Not Sure Which Calculator to Use?</h3>
                <p className="text-gray-700 mb-3 text-sm">We've created a guide to help you choose the right calculator. Learn about the strengths of each, when to upgrade from Basic to Advanced, and pathways based on your age, super balance, and goals.</p>
                <button onClick={() => navigateToPage('selection-guide')} className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors shadow-sm font-medium text-sm">View Calculator Selection Guide</button>
              </div>
            </div>
          </div>
        </div>

        {/* Features Section */}
        <div className="mt-12 bg-white rounded-lg shadow-md p-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center">
            Why SuperCalc Pro Is Different
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="text-center">
              <div className="bg-blue-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Stress-Test Bad Timing</h3>
              <p className="text-gray-600">See how retirement holds up when markets fall early, inflation bites, or major expenses arrive.</p>
            </div>
            <div className="text-center">
              <div className="bg-green-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Australian Retirement Rules</h3>
              <p className="text-gray-600">Model Age Pension settings, super rules, fees, income sources, and household details in Australian terms.</p>
            </div>
            <div className="text-center">
              <div className="bg-purple-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">100 Years of Market History</h3>
              <p className="text-gray-600">Advanced Retirement tests plans against real historical sequences and Monte Carlo paths, not just average returns.</p>
            </div>
          </div>
        </div>

        {/* Disclaimer */}
        <div className="mt-8 bg-yellow-50 border border-yellow-200 rounded-lg p-6">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-yellow-800">
                Important Disclaimer
              </h3>
              <div className="mt-2 text-sm text-yellow-700">
                <p>
                  This calculator is for educational purposes only and should not be considered as financial advice. 
                  Please consult with a qualified financial adviser before making any financial decisions.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Email Signup */}
        <div className="mt-8">
          <EmailSignup context="homepage" />
        </div>
      </div>

      {/* Footer with Legal Links */}
      <footer className="bg-white border-t mt-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
            <div>
              <h3 className="font-semibold text-gray-900 mb-3">Calculators</h3>
              <ul className="space-y-2 text-sm text-gray-600">
                <li><button onClick={() => setCurrentView('working-accumulation')} className="hover:text-blue-600">Accumulation</button></li>
                <li><button onClick={() => setCurrentView('basic')} className="hover:text-blue-600">Basic Retirement</button></li>
                <li><button onClick={() => setCurrentView('advanced')} className="hover:text-blue-600">Advanced Retirement</button></li>
                <li><button onClick={() => setCurrentView('smsf')} className="hover:text-blue-600">SMSF Suite</button></li>
              </ul>
            </div>
            <div>
              <h3 className="font-semibold text-gray-900 mb-3">Support</h3>
              <ul className="space-y-2 text-sm text-gray-600">
                <li><button onClick={() => setCurrentView('help')} className="hover:text-blue-600">Help Center</button></li>
                <li>
                  <button
                    type="button"
                    onClick={() => setShowBillingModal(true)}
                    className="hover:text-blue-600 text-left w-full text-sm text-gray-600"
                  >
                    Billing &amp; subscription
                  </button>
                </li>
                <li><button onClick={() => setCurrentView('privacy')} className="hover:text-blue-600">Privacy Policy</button></li>
                <li><button onClick={() => setCurrentView('terms')} className="hover:text-blue-600">Terms of Service</button></li>
                <li><button onClick={() => setCurrentView('disclaimer')} className="hover:text-blue-600">Disclaimer</button></li>
                <li><button onClick={() => setCurrentView('compliance')} className="hover:text-blue-600">Compliance</button></li>
              </ul>
            </div>
            <div>
              <h3 className="font-semibold text-gray-900 mb-3">Resources</h3>
              <ul className="space-y-2 text-sm text-gray-600">
                <li><a href="/guides/" target="_blank" className="hover:text-blue-600">📚 Planning Guides</a></li>
                <li><a href="https://www.ato.gov.au" target="_blank" rel="noopener noreferrer" className="hover:text-blue-600">ATO Official</a></li>
                <li><a href="https://www.servicesaustralia.gov.au" target="_blank" rel="noopener noreferrer" className="hover:text-blue-600">Services Australia</a></li>
                <li><a href="https://moneysmart.gov.au" target="_blank" rel="noopener noreferrer" className="hover:text-blue-600">ASIC MoneySmart</a></li>
                <li><button onClick={() => setShowDisclaimer(true)} className="hover:text-blue-600">View Disclaimer</button></li>
              </ul>
            </div>
            <div>
              <h3 className="font-semibold text-gray-900 mb-3">About</h3>
              <ul className="space-y-2 text-sm text-gray-600">
                <li>Version 1.0.0</li>
                <li>Live rates · updated from Services Australia / ATO (see What’s new)</li>
                <li className="font-medium text-emerald-700">Rates (Centrelink, ATO, etc.) checked weekly • See “What’s new” above</li>
                <li className="text-xs text-gray-500 mt-4">Educational tool only – Not financial advice</li>
              </ul>
            </div>
          </div>
          <div className="border-t pt-6 text-center">
            <p className="text-sm text-gray-600">
              © 2026 SuperCalc Pro | SuperCalc Australia. All rights reserved.
            </p>
            <p className="text-xs text-gray-500 mt-2">
              Professional retirement planning tools for Australians • Educational purposes only • Not financial advice
            </p>
          </div>
        </div>
      </footer>

      {/* Mandatory Disclaimer Modal */}
      <DisclaimerModal
        isOpen={showDisclaimer}
        onAccept={handleAcceptDisclaimer}
        onDecline={handleDeclineDisclaimer}
      />

      {/* Billing / subscription modal — opened from footer link */}
      {showBillingModal && (
        <SubscriptionManagement onClose={() => setShowBillingModal(false)} />
      )}

    </div>
  );
};

export default MainNavigation;
